/**
 * Library entries the skill's own words point at but it doesn't attach
 * (an exhibit-list template for a skill that builds exhibit lists), each
 * with why it was suggested and an Attach button for whoever may edit.
 */
import { FileText, Paperclip } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";

import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import type {
  FirmSkill,
  SuggestedReference
} from "@/features/firmKnowledge/types";

const ROLE_LABEL: Record<SuggestedReference["role"], string> = {
  shell: "Start from it",
  exemplar: "Imitate it",
  guidance: "Follow it"
};

type Props = {
  skill: FirmSkill;
  skills: FirmSkillsState;
  scope: FirmScope;
};

export const SuggestedReferences: FC<Props> = ({ skill, skills, scope }) => {
  const [suggestions, setSuggestions] = useState<SuggestedReference[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    firmKnowledgeApi
      .getSuggestedReferences(skill.id, scope)
      .then((rows) => {
        if (!cancelled) setSuggestions(rows);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [skill.id, skill.version, skill.references.length, scope]);

  if (suggestions.length === 0) return null;

  const attach = async (s: SuggestedReference) => {
    setBusy(s.entry_id);
    try {
      await skills.update(skill.id, {
        references: [
          ...skill.references.map(({ entry_id, role, note }) => ({
            entry_id,
            role,
            note
          })),
          { entry_id: s.entry_id, role: s.role, note: "" }
        ],
        change_note: `Attached ${s.name}`
      });
      invalidateActiveFirmSkills();
      setSuggestions((cur) => cur.filter((x) => x.entry_id !== s.entry_id));
      toast.success(`Attached ${s.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not attach it.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="skill-suggested-references">
      <p className="text-sm text-fg-3">Suggested from your library</p>
      <ul className="flex flex-col gap-1.5">
        {suggestions.map((s) => (
          <li
            key={s.entry_id}
            className="flex items-start gap-2 rounded-lg border border-dashed border-line-3 px-3 py-2"
            data-testid="skill-suggested-reference"
          >
            <FileText className="mt-0.5 size-4 shrink-0 text-fg-3" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {s.name}
              </span>
              <span className="block text-sm text-fg-3">
                {ROLE_LABEL[s.role]} · {s.why}
              </span>
            </span>
            <Button
              size="xs"
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void attach(s)}
              data-testid={`skill-attach-${s.entry_id}`}
            >
              <Paperclip className="size-3" /> Attach
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
